import useUserStore from "@/state/user/useUserStore";
import React, { useEffect } from "react";
import {
  ArrowBigLeft,
  Eye,
  GraduationCap,
  PawPrint,
  Shirt,
  Smile,
  Star,
} from "lucide-react";
import Footer from "@/components/ui/footer";
import { Asset } from "@/types/types";
import { PostPage } from "@/components/formats/post-page";
import MemeTemplate from "@/components/ui/meme-template";
import UploadMemeTemplate from "@/components/ui/upload-meme-template";
import {
  MemeGrid,
  RouteWrapper,
  TabLogo,
  TitleShadow,
  UploadImageContainer,
} from "@/components/styles";
import Image from "@/components/ui/image";
import { ScrollView, WindowHeader, Window } from "react95";
import { WindowContent } from "react95";
import Link from "next/link";
import { BannerPage } from "@/components/formats/banner-page";

export const BlankPage = () => {
  const [
    selectedImage,
    uploadedImage,
    tabs,
    assetsCount,
    imageFormat,
    setRestrictedFonts,
  ] = useUserStore((state) => [
    state.selectedImage,
    state.uploadedImage,
    state.tabs,
    state.assetsCount,
    state.imageFormat,
    state.setRestrictedFonts,
  ]);

  const [isInIframe, setIsInIframe] = React.useState(false);

  useEffect(() => {
    // Detect if running in iframe
    setIsInIframe(window.self !== window.top);

    setRestrictedFonts([
      { value: "title13", label: "Henlo Clash" },
      { value: "title14", label: "Inter" },
      { value: "title15", label: "Inter Bold" },
    ]);
  }, [setRestrictedFonts]);

  const HeadAssets = Array.from(
    { length: 35 },
    (_, i) => `/Mememaker/henlo3/heads/head${i + 1}.png`
  );

  const BodyAssets = Array.from(
    { length: 3 },
    (_, i) => `/Mememaker/henlo3/bodies/body${i + 1}.png`
  );

  const BeraAssets = Array.from(
    { length: 67 },
    (_, i) => `/Mememaker/henlo3/beras/bera${i + 1}.png`
  );

  const HenloAssets = Array.from(
    { length: 38 },
    (_, i) => `/Mememaker/henlo3/henlo/henlo${i + 1}.png`
  );

  const allAssets: Asset[] = [
    {
      assets: HeadAssets,
      icon: <Smile />,
    },
    { assets: BodyAssets, icon: <Shirt /> },
    { assets: BeraAssets, icon: <PawPrint /> },
    { assets: HenloAssets, icon: <Star /> },
    { assets: [], icon: <Eye /> },
  ];

  const contentInner = (
    <>
      {(() => {
        switch (imageFormat) {
          case "banner":
            return (
              <BannerPage
                color={"#2e64fb"}
                assets={allAssets}
                twitter="henlo"
              />
            );
          case "post":
            return (
              <PostPage color={"#2e64fb"} assets={allAssets} twitter="henlo" />
            );
          default:
            return (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: isInIframe ? "center" : "space-between",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: isInIframe ? "auto" : "auto",
                    display: "flex",
                    flexDirection: "column",
                    boxSizing: "border-box",
                    padding: "36px",
                    gap: "12px",
                    flexGrow: isInIframe ? 0 : 1,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      marginBottom: "16px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "start",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <TitleShadow style={{ margin: 0, marginBottom: "4px" }}>
                        henlo
                      </TitleShadow>
                      <p
                        style={{
                          margin: 0,
                          fontFamily: "var(--font-cabin-condensed)",
                        }}
                      >
                        Select a preset meme template to start creating your
                        meme.
                      </p>
                    </div>
                    {!isInIframe && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <Link
                          style={{
                            margin: 0,
                            fontSize: "16px",
                            fontFamily: "var(--font-cabin-condensed)",
                            whiteSpace: "nowrap",
                            textDecoration: "none",
                            color: "black",
                          }}
                          href="/"
                        >
                          <UploadImageContainer
                            whileHover={{
                              scale: 1.02,
                              transition: {
                                duration: 0.15,
                                ease: "linear",
                              },
                            }}
                            whileTap={{ scale: 0.99 }}
                          >
                            <ArrowBigLeft size={20} />
                            Back
                          </UploadImageContainer>
                        </Link>
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "flex",
                      flexDirection: "row",
                      gap: "16px",
                    }}
                  >
                    <MemeGrid>
                      <MemeTemplate src="/Mememaker/henlo3/background/bg1.png" />
                      <MemeTemplate src="/Mememaker/henlo3/background/bg2.png" />
                      <MemeTemplate src="/Mememaker/henlo3/background/bg3.png" />
                      <MemeTemplate src="/Mememaker/henlo3/background/bg4.png" />
                      <MemeTemplate src="/Mememaker/henlo3/background/bg5.png" />
                      <UploadMemeTemplate />
                    </MemeGrid>
                  </div>
                </div>
                {!isInIframe && <Footer />}
              </div>
            );
        }
      })()}
    </>
  );

  const content = isInIframe ? (
    <div
      style={{
        width: "100%",
        background: "#ddd",
        position: "relative",
        overflow: "auto",
        display: "flex",
      }}
    >
      {contentInner}
    </div>
  ) : (
    <ScrollView
      style={{
        width: "100%",
        flexGrow: 1,
        background: "#ddd",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        maxHeight: "calc(100vh - 62px)",
      }}
    >
      {contentInner}
    </ScrollView>
  );

  // If in iframe, return just the content without window chrome
  if (isInIframe) {
    return (
      <div
        style={{
          width: "100%",
          height: "100vh",
          fontFamily: "var(--font-cabin-condensed)",
          overflow: "hidden",
        }}
      >
        {content}
      </div>
    );
  }

  // Otherwise, return with full window chrome
  return (
    <>
      <RouteWrapper>
        <Window
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            fontFamily: "var(--font-cabin-condensed)",
            overflow: "hidden",
          }}
        >
          <strong>
            <WindowHeader
              className="window-title"
              style={{ background: "#623a00" }}
            >
              <div
                style={{
                  gap: 8,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    position: "relative",
                  }}
                >
                  <Image
                    src={"/Mememaker/beraflip/beraflip.png"}
                    alt={"beraflip"}
                    fill
                    style={{
                      objectFit: "contain",
                    }}
                  />
                </div>
                <span style={{ fontWeight: 500 }}>Beraflip</span>
              </div>
            </WindowHeader>
          </strong>
          <WindowContent
            style={{
              padding: 8,
              boxSizing: "border-box",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {content}
          </WindowContent>
        </Window>
      </RouteWrapper>
    </>
  );
};
